import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import CreatorsPage from "./pages/CreatorsPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import BusinessDashboard from "./pages/BusinessDashboard";
import CreatorDashboard from "./pages/CreatorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CampaignCreate from "./pages/CampaignCreate";
import CampaignDetail from "./pages/CampaignDetail";
import CreatorProfile from "./pages/CreatorProfile";
import TrackingPage from "./pages/TrackingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      // Auth is now handled via httpOnly cookies - no localStorage token needed
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch {
      // Not authenticated - this is normal for new users
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - only depends on setUser, setLoading which are stable

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Backend sets httpOnly cookie - no token stored in localStorage
    const response = await axios.post(
      `${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, userType) => {
    // Backend sets httpOnly cookie - no token stored in localStorage
    const response = await axios.post(
      `${API}/auth/register`,
      {
        email,
        password,
        name,
        user_type: userType,
      },
      { withCredentials: true }
    );
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const googleLogin = (userType = "creator") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    localStorage.setItem("pending_user_type", userType);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const setGoogleUser = (userData) => {
    // Session is stored in httpOnly cookie by backend
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem("pending_user_type");
    setUser(null);
  };

  // Expose setUser for components that need to update user state
  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const value = {
    user,
    setUser: updateUser,
    loading,
    login,
    register,
    googleLogin,
    setGoogleUser,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#05060D]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(user.user_type)) {
    // Redirect to appropriate dashboard
    if (user.user_type === "business") {
      return <Navigate to="/dashboard" replace />;
    } else if (user.user_type === "creator") {
      return <Navigate to="/creator" replace />;
    } else if (user.user_type === "admin") {
      return <Navigate to="/admin" replace />;
    }
  }

  return children;
};

// App Router with OAuth callback detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id - CRITICAL: detect during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/creators" element={<CreatorsPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/t/:trackingCode" element={<TrackingPage />} />
      
      {/* Business Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedTypes={["business"]}>
            <BusinessDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/campaigns/new"
        element={
          <ProtectedRoute allowedTypes={["business"]}>
            <CampaignCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/create"
        element={
          <ProtectedRoute allowedTypes={["business"]}>
            <CampaignCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/campaigns/:campaignId"
        element={
          <ProtectedRoute allowedTypes={["business"]}>
            <CampaignDetail />
          </ProtectedRoute>
        }
      />

      {/* Creator Routes */}
      <Route
        path="/creator"
        element={
          <ProtectedRoute allowedTypes={["creator"]}>
            <CreatorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/profile"
        element={
          <ProtectedRoute allowedTypes={["creator"]}>
            <CreatorProfile />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
export { API, BACKEND_URL };
